import React, { memo, useState, useEffect, useRef } from 'react';
import { LABEL_TYPES } from '../utils/labelConstants';
import {
  tsToDateInput,
  tsToLabel,
  dateInputToTs,
  findDataForFormula,
  sensorPositionFormulaToLabel,
} from '../utils/labelUtils';
import { GeoIcon } from '../utils/LabelIcons';

const LabelEditDialog = memo(
  ({ label, data, sensorsPositions, onSave, onCancel, error }) => {
    const [dateOptions, setDateOptions] = useState([]);
    const [draft, setDraft] = useState({ ...label });
    const dateSelectRef = useRef(null);

    useEffect(() => {
      // Lors de l'édition d'un label existant, reconstruire la formule capteur/position
      // depuis idSensor + positionInSensor — sinon on retombe sur la 1re courbe (souvent
      // un AUTRE capteur sur un graphe IA), ce qui change le capteur du label et provoque
      // une violation de contrainte unique (id_sensor, position, time) côté serveur.
      const matched =
        label.idSensor != null
          ? (sensorsPositions || []).find((sp) => {
              const nums = (sp.formula || '').match(/\d+/g)?.map(Number);
              return (
                nums?.length >= 2 &&
                nums[0] === Number(label.idSensor) &&
                nums[1] === Number(label.positionInSensor)
              );
            })
          : null;
      const builtFormula =
        label.idSensor != null && label.positionInSensor != null
          ? `#${label.idSensor},${label.positionInSensor}#`
          : null;
      const preferred =
        label.sensorAndPosition ||
        matched?.formula ||
        builtFormula ||
        sensorsPositions?.[0]?.formula;
      if (!preferred) return;

      setDraft((d) => ({ ...d, sensorAndPosition: preferred }));

      const allFormulas = [
        preferred,
        ...(sensorsPositions || []).map((sp) => sp.formula),
      ].filter(Boolean);

      for (const formula of allFormulas) {
        const found = findDataForFormula(data, sensorsPositions, formula);
        const tArr = found?.x || found?.t || [];
        if (tArr.length) {
          const opts = tArr.map((ts, i) => ({
            ts: Number(ts),
            y: found.y[i],
            pk: found.pk?.[i] ?? i,
          }));

          // 1. Use pointIndex (direct array index from the click) when available —
          //    avoids any timestamp format/priority mismatch.
          // 2. Fallback: find closest by numeric timestamp comparison.
          let bestIdx = 0;
          if (label.pointIndex != null && label.pointIndex < opts.length) {
            bestIdx = label.pointIndex;
          } else {
            const targetT = Number(label.t);
            let bestDiff = Infinity;
            opts.forEach(({ ts }, i) => {
              const d = Math.abs(ts - targetT);
              if (d < bestDiff) { bestDiff = d; bestIdx = i; }
            });
          }

          setDateOptions(opts);
          setDraft((d) => ({ ...d, t: opts[bestIdx].ts, pk: opts[bestIdx].pk }));
          return;
        }
      }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Scroll the select to the selected option after dateOptions load.
    useEffect(() => {
      const sel = dateSelectRef.current;
      if (!sel || !dateOptions.length) return;
      const selectedIdx = sel.selectedIndex >= 0 ? sel.selectedIndex : 0;
      const itemH = sel.scrollHeight / sel.options.length;
      sel.scrollTop = Math.max(0, selectedIdx * itemH - sel.clientHeight / 2);
    }, [dateOptions]);

    const setField = (field, value) => {
      if (field === 'sensorAndPosition') {
        const found = findDataForFormula(data, sensorsPositions, value);
        if (found?.x?.length) {
          setDateOptions(found.x.map((ts, i) => ({ ts, y: found.y[i], pk: found.pk?.[i] ?? i })));
        } else {
          setDateOptions([]);
        }
        setDraft((d) => ({ ...d, sensorAndPosition: value }));
        return;
      }

      if (field === 'labelType') {
        setDraft((d) => ({
          ...d,
          labelType: value,
          iconType: LABEL_TYPES[value].iconType,
        }));
      } else {
        setDraft((d) => ({
          ...d,
          [field]: field === 't' ? Number(value) : value,
        }));
      }
    };

    const canSave =
      !!draft.sensorAndPosition &&
      !!draft.t &&
      draft.label?.trim().length > 0;

    return (
      <div className="leg-overlay" onMouseDown={onCancel}>
        <div className="leg-dialog" onMouseDown={(e) => e.stopPropagation()}>
          <div className="leg-dialog-header">
            <div className="leg-dialog-title-row">
              <GeoIcon iconType={draft.iconType} size={15} />
              <span className="leg-dialog-title">Éditer le label</span>
            </div>
            <button className="leg-dialog-close" onClick={onCancel}>
              ✕
            </button>
          </div>
          <div className="leg-form-grid">
            <span className="leg-field-label">Courbe</span>
            <select
              value={draft.sensorAndPosition}
              onChange={(e) => setField('sensorAndPosition', e.target.value)}
              size={4}
              className="leg-select-list"
            >
              {sensorsPositions.map(({ formula }) => {
                const curve = findDataForFormula(data, sensorsPositions, formula);
                const label = curve?.name || curve?.sensor_name || sensorPositionFormulaToLabel(formula);
                return (
                  <option key={formula} value={formula}>{label}</option>
                );
              })}
            </select>

            <span className="leg-field-label">Date</span>
            {dateOptions.length > 0 ? (
              <select
                ref={dateSelectRef}
                value={draft.t}
                onChange={(e) => {
                  const opt = dateOptions.find((o) => o.ts === Number(e.target.value));
                  setDraft((d) => ({ ...d, t: opt.ts, pk: opt.pk }));
                }}
                size={4}
                className="leg-select-list"
              >
                {dateOptions.map(({ ts, y }) => (
                  <option key={ts} value={ts}>
                    {tsToLabel(ts, y)}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="date"
                value={draft.t ? tsToDateInput(draft.t) : ''}
                onChange={(e) => setField('t', dateInputToTs(e.target.value))}
                className="leg-input"
              />
            )}
            <span className="leg-field-label">Label</span>
            <input
              type="text"
              value={draft.label || ''}
              placeholder="label…"
              onChange={(e) => setField('label', e.target.value)}
              className="leg-input"
            />
          </div>
          {error && (
            <div className="leg-dialog-error">{error}</div>
          )}
          <div className="leg-dialog-actions">
            <button className="leg-btn-clear" onClick={onCancel}>
              Annuler
            </button>
            <button
              className="leg-btn-add"
              onClick={() => onSave(draft)}
              disabled={!canSave}
            >
              Enregistrer
            </button>
          </div>
        </div>
      </div>
    );
  }
);

export default LabelEditDialog;
